import { Button, Modal, Text, View } from '@zinc/ui';
import type React from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

export enum AreYouSureText {
  Default = 'Are you sure you want to do this?',
  Delete = 'Are you sure you want to delete this?',
  Unsaved = 'You have unsaved changes. Would you like to save them before closing?'
}

type AreYouSureOptions = {
  title?: string;
  text?: string | AreYouSureText;
  yesText?: string;
  noText?: string;
  showCancelButton?: boolean;
  hideNoButton?: boolean;
  onYesColor?: 'primary' | 'critical' | 'positive' | 'neutral' | 'media' | 'inherit' | undefined;
  onYes?: () => void;
  onNo?: () => void;
  size?: string;
};

type AreYouSureContextType = {
  onOpen: (options?: AreYouSureOptions) => void;
};

const AreYouSureContext = createContext<AreYouSureContextType | undefined>(undefined);

export const useAreYouSure = () => {
  const ctx = useContext(AreYouSureContext);
  if (!ctx) throw new Error('useAreYouSure must be used within AreYouSureProvider');
  return ctx;
};

export const AreYouSureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AreYouSureOptions>({});
  const { hideNoButton } = options;
  const onOpen = useCallback((opts?: AreYouSureOptions) => {
    setOptions(opts || {});
    setOpen(true);
  }, []);

  const handleYes = useCallback(() => {
    options.onYes?.();
    setOpen(false);
  }, [options]);

  const handleNo = useCallback(() => {
    if (options.hideNoButton) return;
    options.onNo?.();
    setOpen(false);
  }, [options]);

  const onCancel = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <AreYouSureContext.Provider value={{ onOpen }}>
      {children}
      <Modal active={open} attributes={{ id: 'are-you-sure-modal' }} size={options.size}>
        <View padding={2} justify="center" gap={4}>
          <Modal.Title>{options.title || 'Are You Sure?'}</Modal.Title>
          <Text>{options.text || AreYouSureText.Default}</Text>
          <View
            direction={{ s: 'column', m: 'row' }}
            justify={{ s: 'center', m: 'end' }}
            wrap={{ s: true, m: false }}
            align="center"
            gap={2}
          >
            {options.showCancelButton && (
              <Button fullWidth={{ s: true, m: false }} variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <View width={{ s: '100%', m: 'auto' }}>
              {!hideNoButton && (
                <Button fullWidth={{ s: true, m: false }} variant="outline" onClick={handleNo}>
                  {options.noText || 'No'}
                </Button>
              )}
            </View>
            <View width={{ s: '100%', m: 'auto' }}>
              <Button
                fullWidth={{ s: true, m: false }}
                color={options.onYesColor || 'primary'}
                onClick={handleYes}
              >
                {options.yesText || 'Yes'}
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </AreYouSureContext.Provider>
  );
};
